import { useState, useEffect } from "react";
import { CaretRight, Check, DotsSixVertical, CaretDoubleDown, CaretDoubleUp } from "@phosphor-icons/react";
import { pillarStyle, normalizeSteps, getHorizons, deriveStatus, STATUS_META } from "@/lib/planiq";

function KanbanCard({ card, pillar, onClick, onDragStart, onDragEnd, onDragOverCard, onCardDrop, onToggleStep, dragging, indicator, expandSignal }) {
  const [open, setOpen] = useState(false);
  const style = pillarStyle(pillar);
  const steps = normalizeSteps(card.steps);
  const done = steps.filter((s) => s.done).length;
  const status = deriveStatus(steps);
  const meta = STATUS_META[status];

  // Respond to board-level Expand all / Collapse all.
  useEffect(() => {
    if (expandSignal) setOpen(expandSignal.open);
  }, [expandSignal]);

  return (
    <div
      className="relative"
      onDragOver={(e) => onDragOverCard(e, card)}
      onDrop={(e) => onCardDrop(e, card)}
    >
      {indicator === "before" && <div className="absolute -top-1.5 left-0 right-0 h-0.5 bg-gray-900 z-10" data-testid="drop-indicator" />}
      <div
        draggable
        onDragStart={(e) => onDragStart(e, card)}
        onDragEnd={onDragEnd}
        onClick={() => onClick(card)}
        data-testid={`kanban-card-${card._id}`}
        className={`card-hover group/card bg-white border border-gray-200 border-l-4 p-3.5 cursor-pointer hover:border-gray-400 transition-opacity ${dragging ? "opacity-40" : ""}`}
        style={{ borderLeftColor: style.hex }}
      >
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5"
            style={{ color: style.hex, backgroundColor: style.bg }}>
            {pillar?.name || "Unassigned"}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{ color: meta.hex, backgroundColor: meta.bg }}
              data-testid={`card-status-pill-${card._id}`} title={meta.label}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.hex }} />
              {meta.label}
            </span>
            <DotsSixVertical size={14} weight="bold" className="text-gray-300 group-hover/card:text-gray-500 cursor-grab active:cursor-grabbing" aria-label="Drag to reorder" />
          </span>
        </div>
        <p className={`text-sm leading-snug font-medium ${status === "complete" ? "text-gray-400 line-through" : "text-gray-900"}`}>
          {card.title}
        </p>

        {steps.length > 0 && (
          <div className="mt-2.5 pt-2.5 border-t border-gray-100">
            <button
              onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
              data-testid={`expand-card-${card._id}`}
              className="flex items-center gap-1.5 text-[11px] font-mono text-gray-500 hover:text-gray-900 transition-colors"
            >
              <CaretRight size={12} weight="bold" className={`transition-transform ${open ? "rotate-90" : ""}`} />
              <span style={done === steps.length ? { color: style.hex } : {}}>{done}/{steps.length} sub-tasks</span>
            </button>

            {open && (
              <ul className="mt-2.5 space-y-2" onClick={(e) => e.stopPropagation()} data-testid={`steps-${card._id}`}>
                {steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); onToggleStep(card._id, i); }}
                      data-testid={`step-toggle-${card._id}-${i}`}
                      aria-label={s.done ? "Mark incomplete" : "Mark complete"}
                      className={`mt-0.5 w-4 h-4 border flex items-center justify-center shrink-0 transition-colors ${s.done ? "bg-gray-950 border-gray-950" : "border-gray-300 bg-white hover:border-gray-900"}`}
                    >
                      {s.done && <Check size={11} weight="bold" className="text-white" />}
                    </button>
                    <span className={`text-xs leading-snug ${s.done ? "line-through text-gray-400" : "text-gray-700"}`}>
                      {s.text}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {indicator === "after" && <div className="absolute -bottom-1.5 left-0 right-0 h-0.5 bg-gray-900 z-10" data-testid="drop-indicator" />}
    </div>
  );
}

export default function KanbanBoard({ cards, pillarById, onCardClick, onReorder, onToggleStep, planType }) {
  const [dragId, setDragId] = useState(null);
  const [dragHorizon, setDragHorizon] = useState(null);
  const [overId, setOverId] = useState(null);
  const [overPos, setOverPos] = useState(null);
  const [filterId, setFilterId] = useState(null);
  const [expandSignal, setExpandSignal] = useState(null); // {open, key}
  const horizons = getHorizons(planType);
  const pillarList = Object.values(pillarById);
  const hasSteps = cards.some((c) => normalizeSteps(c.steps).length > 0);

  const reset = () => { setDragId(null); setDragHorizon(null); setOverId(null); setOverPos(null); };

  // Full, order-sorted card-id list for one horizon (independent of any active filter)
  // so reordering never drops the cards a filter is hiding.
  const horizonIds = (horizon) =>
    cards.filter((c) => c.horizon === horizon)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((c) => c._id);

  const onDragStart = (e, card) => {
    setDragId(card._id);
    setDragHorizon(card.horizon);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOverCard = (e, card) => {
    if (!dragId || card.horizon !== dragHorizon) return; // reorder within the same horizon only
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const after = e.clientY > rect.top + rect.height / 2;
    setOverId(card._id);
    setOverPos(after ? "after" : "before");
  };

  const onCardDrop = (e, card) => {
    if (!dragId || card.horizon !== dragHorizon) { reset(); return; }
    e.preventDefault();
    e.stopPropagation();
    if (dragId !== card._id) {
      const ids = horizonIds(card.horizon).filter((id) => id !== dragId);
      let ti = ids.indexOf(card._id);
      if (overPos === "after") ti += 1;
      ids.splice(ti, 0, dragId);
      onReorder(card.horizon, ids);
    }
    reset();
  };

  const onColDrop = (e, horizon) => {
    if (dragId && horizon === dragHorizon) {
      const ids = horizonIds(horizon).filter((id) => id !== dragId);
      ids.push(dragId); // dropped in empty space -> send to the bottom
      onReorder(horizon, ids);
    }
    reset();
  };

  return (
    <div className="h-full flex flex-col">
      {(pillarList.length > 0 || hasSteps) && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="board-toolbar">
          {pillarList.length > 0 && (
            <>
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-400 mr-1">Filter</span>
              <button onClick={() => setFilterId(null)} data-testid="pillar-filter-all"
                className={`text-xs font-semibold px-3 py-1.5 border transition-colors ${!filterId ? "bg-gray-950 text-white border-gray-950" : "border-gray-300 text-gray-600 hover:border-gray-900"}`}>
                All
              </button>
              {pillarList.map((p) => {
                const s = pillarStyle(p);
                const active = filterId === p.id;
                return (
                  <button key={p.id} onClick={() => setFilterId(active ? null : p.id)}
                    data-testid={`pillar-filter-${p.id}`}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 border transition-colors ${active ? "text-white border-transparent" : "text-gray-700 border-gray-300 hover:border-gray-900"}`}
                    style={active ? { backgroundColor: s.hex } : {}}>
                    <span className="w-2.5 h-2.5" style={{ backgroundColor: s.hex }} />
                    {p.name}
                  </button>
                );
              })}
            </>
          )}
          {hasSteps && (
            <div className="ml-auto flex items-center gap-2">
              <button onClick={() => setExpandSignal({ open: true, key: Date.now() })} data-testid="expand-all-button"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 border border-gray-300 text-gray-600 hover:border-gray-900 hover:text-gray-900 transition-colors">
                <CaretDoubleDown size={13} weight="bold" /> Expand all
              </button>
              <button onClick={() => setExpandSignal({ open: false, key: Date.now() })} data-testid="collapse-all-button"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 border border-gray-300 text-gray-600 hover:border-gray-900 hover:text-gray-900 transition-colors">
                <CaretDoubleUp size={13} weight="bold" /> Collapse all
              </button>
            </div>
          )}
        </div>
      )}
      <div className={`kanban-grid kanban-cols-${horizons.length} flex-1`}>
        {horizons.map((h) => {
          const colCards = cards
            .filter((c) => c.horizon === h.key && (!filterId || c.pillar_id === filterId))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          const isDropTarget = dragHorizon === h.key;
          return (
            <div
              key={h.key}
              data-testid={`kanban-column-${h.key}`}
              onDragOver={(e) => { if (isDropTarget) { e.preventDefault(); if (e.target === e.currentTarget) setOverId(null); } }}
              onDrop={(e) => onColDrop(e, h.key)}
              className={`bg-gray-50 border flex flex-col min-h-[300px] ${isDropTarget && dragId ? "border-gray-900" : "border-gray-200"}`}
            >
              <div className="px-4 pt-4 pb-3 border-b border-gray-200">
                <div className="flex items-baseline justify-between">
                  <h2 className="font-display font-bold text-lg tracking-tight">{h.label}</h2>
                  <span className="font-mono text-xs text-gray-400">{colCards.length}</span>
                </div>
                <p className="text-xs font-semibold text-gray-800 mt-0.5">{h.posture}</p>
                <p className="text-[10px] uppercase tracking-wider text-gray-400 font-mono">{h.sub}</p>
              </div>
              <div className="p-3 space-y-3 flex-1">
                {colCards.map((c) => (
                  <KanbanCard key={c._id} card={c} pillar={pillarById[c.pillar_id]}
                    onClick={onCardClick} onDragStart={onDragStart} onDragEnd={reset}
                    onDragOverCard={onDragOverCard} onCardDrop={onCardDrop} onToggleStep={onToggleStep}
                    dragging={dragId === c._id}
                    indicator={overId === c._id ? overPos : null} expandSignal={expandSignal} />
                ))}
                {colCards.length === 0 && (
                  <p className="text-xs text-gray-400 text-center py-8 font-mono">
                    {filterId ? "No actions for this pillar" : "No actions in this horizon"}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
